import React, { useState } from 'react';
import { SeedreamAPI } from '../services/api';
import { downloadImage } from '../utils/downloadUtils';
import { handleImageUpload, triggerFileSelection } from '../utils/uploadUtils';

const Generate = () => {
  const [prompt, setPrompt] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [referenceImages, setReferenceImages] = useState(['', '']);
  const [generationType, setGenerationType] = useState('single');
  const [maxImages, setMaxImages] = useState(4);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [error, setError] = useState('');
  const [downloadingIndex, setDownloadingIndex] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingReference, setUploadingReference] = useState({ 0: false, 1: false });

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      setError('Please enter a prompt');
      return;
    }

    setLoading(true);
    setError('');
    setResults([]);

    try {
      let response;
      
      switch (generationType) {
        case 'single':
          response = await SeedreamAPI.generateSingleImage(prompt);
          break;
        case 'multiple':
          response = await SeedreamAPI.generateMultipleImages(prompt, maxImages);
          break;
        case 'edit':
          if (!imageUrl.trim()) {
            setError('Please provide an image URL for editing');
            setLoading(false);
            return;
          }
          response = await SeedreamAPI.editImage(prompt, imageUrl);
          break;
        case 'expand':
          if (!imageUrl.trim()) {
            setError('Please provide an image URL for expansion');
            setLoading(false);
            return;
          }
          response = await SeedreamAPI.expandImageToMultiples(prompt, imageUrl, maxImages);
          break;
        case 'reference':
          if (!referenceImages[0].trim() || !referenceImages[1].trim()) {
            setError('Please provide both reference image URLs');
            setLoading(false);
            return;
          }
          response = await SeedreamAPI.generateWithReferenceImages(prompt, referenceImages);
          break;
        default:
          throw new Error('Invalid generation type');
      }

      if (response.data) {
        setResults(Array.isArray(response.data) ? response.data : [response.data]);
      }
    } catch (err) {
      setError(err.message || 'Failed to generate image');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (imageUrl, index) => {
    try {
      setDownloadingIndex(index);
      await downloadImage(imageUrl);
    } catch (error) {
      console.error('Download failed:', error);
      // You could add a toast notification here
      alert('Download failed. Please try again.');
    } finally {
      setDownloadingIndex(null);
    }
  };

  const handleImageFileUpload = () => {
    triggerFileSelection((file) => {
      setUploadingImage(true);
      setError('');
      
      handleImageUpload(
        file,
        (dataURL) => {
          setImageUrl(dataURL);
          setUploadingImage(false);
        },
        (errorMessage) => {
          setError(errorMessage);
          setUploadingImage(false);
        }
      );
    });
  };

  const handleReferenceImageUpload = (index) => {
    triggerFileSelection((file) => {
      setUploadingReference(prev => ({ ...prev, [index]: true }));
      setError('');
      
      handleImageUpload(
        file,
        (dataURL) => {
          const newReferenceImages = [...referenceImages];
          newReferenceImages[index] = dataURL;
          setReferenceImages(newReferenceImages);
          setUploadingReference(prev => ({ ...prev, [index]: false }));
        },
        (errorMessage) => {
          setError(errorMessage);
          setUploadingReference(prev => ({ ...prev, [index]: false }));
        }
      );
    });
  };

  return (
    <div className="px-40 flex flex-1 justify-center py-5">
      <div className="layout-content-container flex flex-col max-w-[960px] flex-1">
        <div className="flex flex-col gap-6 px-4 py-6">
          <h1 className="text-white text-[32px] font-bold leading-tight tracking-[-0.015em]">
            Generate Images
          </h1>
          
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <label className="text-white text-sm font-medium">Generation Type</label>
              <select
                value={generationType}
                onChange={(e) => setGenerationType(e.target.value)}
                className="form-input flex w-full resize-none overflow-hidden rounded-xl text-white focus:outline-0 focus:ring-0 border-none bg-[#2e2839] h-14 placeholder:text-[#a69db9] p-4 text-base font-normal leading-normal"
              >
                <option value="single">Text to Single Image</option>
                <option value="multiple">Text to Multiple Images</option>
                <option value="edit">Edit Image</option>
                <option value="expand">Expand to Multiple Images</option>
                <option value="reference">Generate with Reference Images</option>
              </select>
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-white text-sm font-medium">Prompt</label>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Describe what you want to generate..."
                className="form-input flex w-full resize-none overflow-hidden rounded-xl text-white focus:outline-0 focus:ring-0 border-none bg-[#2e2839] min-h-36 placeholder:text-[#a69db9] p-4 text-base font-normal leading-normal"
              />
            </div>

            {(generationType === 'edit' || generationType === 'expand') && (
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <label className="text-white text-sm font-medium">Image URL</label>
                  <div className="relative group">
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      width="16" 
                      height="16" 
                      fill="currentColor" 
                      viewBox="0 0 256 256"
                      className="text-[#a69db9] hover:text-white cursor-help transition-colors duration-200"
                    >
                      <path d="M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm0,192a88,88,0,1,1,88-88A88.1,88.1,0,0,1,128,216Zm16-40a8,8,0,0,1-8,8,16,16,0,0,1-16-16V128a8,8,0,0,1,0-16,16,16,0,0,1,16,16v40A8,8,0,0,1,144,176ZM112,84a12,12,0,1,1,12,12A12,12,0,0,1,112,84Z"/>
                    </svg>
                    <div className="absolute left-1/2 transform -translate-x-1/2 bottom-full mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                      Please ensure the image URL is publicly accessible
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <input
                    type="url"
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                    placeholder="https://example.com/image.jpg"
                    className="form-input flex flex-1 resize-none overflow-hidden rounded-xl text-white focus:outline-0 focus:ring-0 border-none bg-[#2e2839] h-14 placeholder:text-[#a69db9] p-4 text-base font-normal leading-normal"
                  />
                  <button
                    onClick={handleImageFileUpload}
                    disabled={uploadingImage}
                    className="flex items-center justify-center px-4 py-2 bg-[#7c3aed] hover:bg-[#6d28d9] disabled:bg-[#4c1d95] disabled:cursor-not-allowed text-white rounded-xl transition-colors duration-200 min-w-[100px] h-14"
                  >
                    {uploadingImage ? (
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span className="text-sm">Uploading...</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <svg 
                          xmlns="http://www.w3.org/2000/svg" 
                          width="16" 
                          height="16" 
                          fill="currentColor" 
                          viewBox="0 0 256 256"
                        >
                          <path d="M216,40H40A16,16,0,0,0,24,56V200a16,16,0,0,0,16,16H216a16,16,0,0,0,16-16V56A16,16,0,0,0,216,40ZM40,56H216v58.75l-26.07-26.06a16,16,0,0,0-22.63,0l-20,20-44-44a16,16,0,0,0-22.62,0L40,105.37ZM216,200H40V129.37l40-40,44,44a16,16,0,0,0,22.62,0l20-20L216,162.75V200ZM144,100a12,12,0,1,1,12,12A12,12,0,0,1,144,100Z"/>
                        </svg>
                        <span className="text-sm">Upload</span>
                      </div>
                    )}
                  </button>
                </div>
              </div>
            )}

            {generationType === 'reference' && (
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <label className="text-white text-sm font-medium">Reference Image 1 URL</label>
                    <div className="relative group">
                      <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        width="16" 
                        height="16" 
                        fill="currentColor" 
                        viewBox="0 0 256 256"
                        className="text-[#a69db9] hover:text-white cursor-help transition-colors duration-200"
                      >
                        <path d="M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm0,192a88,88,0,1,1,88-88A88.1,88.1,0,0,1,128,216Zm16-40a8,8,0,0,1-8,8,16,16,0,0,1-16-16V128a8,8,0,0,1,0-16,16,16,0,0,1,16,16v40A8,8,0,0,1,144,176ZM112,84a12,12,0,1,1,12,12A12,12,0,0,1,112,84Z"/>
                      </svg>
                      <div className="absolute left-1/2 transform -translate-x-1/2 bottom-full mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                        Please ensure the image URL is publicly accessible
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="url"
                      value={referenceImages[0]}
                      onChange={(e) => setReferenceImages([e.target.value, referenceImages[1]])}
                      placeholder="https://example.com/reference1.jpg"
                      className="form-input flex flex-1 resize-none overflow-hidden rounded-xl text-white focus:outline-0 focus:ring-0 border-none bg-[#2e2839] h-14 placeholder:text-[#a69db9] p-4 text-base font-normal leading-normal"
                    />
                    <button
                      onClick={() => handleReferenceImageUpload(0)}
                      disabled={uploadingReference[0]}
                      className="flex items-center justify-center px-4 py-2 bg-[#7c3aed] hover:bg-[#6d28d9] disabled:bg-[#4c1d95] disabled:cursor-not-allowed text-white rounded-xl transition-colors duration-200 min-w-[100px] h-14"
                    >
                      {uploadingReference[0] ? (
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          <span className="text-sm">Uploading...</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <svg 
                            xmlns="http://www.w3.org/2000/svg" 
                            width="16" 
                            height="16" 
                            fill="currentColor" 
                            viewBox="0 0 256 256"
                          >
                            <path d="M216,40H40A16,16,0,0,0,24,56V200a16,16,0,0,0,16,16H216a16,16,0,0,0,16-16V56A16,16,0,0,0,216,40ZM40,56H216v58.75l-26.07-26.06a16,16,0,0,0-22.63,0l-20,20-44-44a16,16,0,0,0-22.62,0L40,105.37ZM216,200H40V129.37l40-40,44,44a16,16,0,0,0,22.62,0l20-20L216,162.75V200ZM144,100a12,12,0,1,1,12,12A12,12,0,0,1,144,100Z"/>
                          </svg>
                          <span className="text-sm">Upload</span>
                        </div>
                      )}
                    </button>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <label className="text-white text-sm font-medium">Reference Image 2 URL</label>
                    <div className="relative group">
                      <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        width="16" 
                        height="16" 
                        fill="currentColor" 
                        viewBox="0 0 256 256"
                        className="text-[#a69db9] hover:text-white cursor-help transition-colors duration-200"
                      >
                        <path d="M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm0,192a88,88,0,1,1,88-88A88.1,88.1,0,0,1,128,216Zm16-40a8,8,0,0,1-8,8,16,16,0,0,1-16-16V128a8,8,0,0,1,0-16,16,16,0,0,1,16,16v40A8,8,0,0,1,144,176ZM112,84a12,12,0,1,1,12,12A12,12,0,0,1,112,84Z"/>
                      </svg>
                      <div className="absolute left-1/2 transform -translate-x-1/2 bottom-full mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                        Please ensure the image URL is publicly accessible
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="url"
                      value={referenceImages[1]}
                      onChange={(e) => setReferenceImages([referenceImages[0], e.target.value])}
                      placeholder="https://example.com/reference2.jpg"
                      className="form-input flex flex-1 resize-none overflow-hidden rounded-xl text-white focus:outline-0 focus:ring-0 border-none bg-[#2e2839] h-14 placeholder:text-[#a69db9] p-4 text-base font-normal leading-normal"
                    />
                    <button
                      onClick={() => handleReferenceImageUpload(1)}
                      disabled={uploadingReference[1]}
                      className="flex items-center justify-center px-4 py-2 bg-[#7c3aed] hover:bg-[#6d28d9] disabled:bg-[#4c1d95] disabled:cursor-not-allowed text-white rounded-xl transition-colors duration-200 min-w-[100px] h-14"
                    >
                      {uploadingReference[1] ? (
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          <span className="text-sm">Uploading...</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <svg 
                            xmlns="http://www.w3.org/2000/svg" 
                            width="16" 
                            height="16" 
                            fill="currentColor" 
                            viewBox="0 0 256 256"
                          >
                            <path d="M216,40H40A16,16,0,0,0,24,56V200a16,16,0,0,0,16,16H216a16,16,0,0,0,16-16V56A16,16,0,0,0,216,40ZM40,56H216v58.75l-26.07-26.06a16,16,0,0,0-22.63,0l-20,20-44-44a16,16,0,0,0-22.62,0L40,105.37ZM216,200H40V129.37l40-40,44,44a16,16,0,0,0,22.62,0l20-20L216,162.75V200ZM144,100a12,12,0,1,1,12,12A12,12,0,0,1,144,100Z"/>
                          </svg>
                          <span className="text-sm">Upload</span>
                        </div>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {(generationType === 'multiple' || generationType === 'expand') && (
              <div className="flex flex-col gap-2">
                <label className="text-white text-sm font-medium">Number of Images (max 5)</label>
                <input
                  type="number"
                  min="1"
                  max="5"
                  value={maxImages}
                  onChange={(e) => setMaxImages(parseInt(e.target.value))}
                  className="form-input flex w-full resize-none overflow-hidden rounded-xl text-white focus:outline-0 focus:ring-0 border-none bg-[#2e2839] h-14 placeholder:text-[#a69db9] p-4 text-base font-normal leading-normal"
                />
              </div>
            )}

            <button
              onClick={handleGenerate}
              disabled={loading}
              className="flex min-w-[84px] w-full cursor-pointer items-center justify-center overflow-hidden rounded-xl h-12 px-5 bg-[#5b13ec] text-white text-base font-bold leading-normal tracking-[0.015em] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="loading-spinner" />
                  Generating...
                </div>
              ) : (
                'Generate'
              )}
            </button>

            {error && (
              <div className="bg-red-500/20 border border-red-500 rounded-xl p-4">
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}
          </div>

          {results.length > 0 && (
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <h2 className="text-white text-xl font-bold">Generated Images</h2>
                {results.length > 1 && (
                  <button
                    onClick={() => {
                      results.forEach((result, index) => {
                        setTimeout(() => handleDownload(result.url || result, index), index * 500);
                      });
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-[#5b13ec] hover:bg-[#4a0fd1] text-white text-sm font-medium rounded-lg transition-colors duration-200"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 256 256">
                      <path d="M224,152v56a16,16,0,0,1-16,16H48a16,16,0,0,1-16-16V152a8,8,0,0,1,16,0v56H208V152a8,8,0,0,1,16,0ZM101.66,122.34a8,8,0,0,0,11.31,0L128,107.31V184a8,8,0,0,0,16,0V107.31l15.03,15.03a8,8,0,1,0,11.31-11.31l-28.68-28.68a8,8,0,0,0-11.32,0L101.66,111.03A8,8,0,0,0,101.66,122.34Z"/>
                    </svg>
                    Download All
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {results.map((result, index) => (
                  <div key={index} className="flex flex-col gap-3">
                    <div className="relative group">
                      <div
                        className="aspect-square bg-cover bg-center bg-no-repeat rounded-xl border border-[#2e2839] transition-transform duration-200 group-hover:scale-[1.02]"
                        style={{
                          backgroundImage: `url("${result.url || result}")`
                        }}
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 rounded-xl transition-all duration-200 flex items-center justify-center">
                        <button
                          onClick={() => handleDownload(result.url || result, index)}
                          disabled={downloadingIndex === index}
                          className="opacity-0 group-hover:opacity-100 flex items-center gap-2 px-4 py-2 bg-[#5b13ec] hover:bg-[#4a0fd1] text-white text-sm font-medium rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {downloadingIndex === index ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              Downloading...
                            </>
                          ) : (
                            <>
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 256 256">
                                <path d="M224,152v56a16,16,0,0,1-16,16H48a16,16,0,0,1-16-16V152a8,8,0,0,1,16,0v56H208V152a8,8,0,0,1,16,0ZM101.66,122.34a8,8,0,0,0,11.31,0L128,107.31V184a8,8,0,0,0,16,0V107.31l15.03,15.03a8,8,0,1,0,11.31-11.31l-28.68-28.68a8,8,0,0,0-11.32,0L101.66,111.03A8,8,0,0,0,101.66,122.34Z"/>
                              </svg>
                              Download
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        {result.revised_prompt && (
                          <p className="text-[#a69db9] text-sm">
                            Revised: {result.revised_prompt}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => handleDownload(result.url || result, index)}
                        disabled={downloadingIndex === index}
                        className="flex items-center gap-1 px-3 py-1.5 bg-[#2e2839] hover:bg-[#3a3447] text-white text-xs font-medium rounded-md transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {downloadingIndex === index ? (
                          <>
                            <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin" />
                            <span>Downloading</span>
                          </>
                        ) : (
                          <>
                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 256 256">
                              <path d="M224,152v56a16,16,0,0,1-16,16H48a16,16,0,0,1-16-16V152a8,8,0,0,1,16,0v56H208V152a8,8,0,0,1,16,0ZM101.66,122.34a8,8,0,0,0,11.31,0L128,107.31V184a8,8,0,0,0,16,0V107.31l15.03,15.03a8,8,0,1,0,11.31-11.31l-28.68-28.68a8,8,0,0,0-11.32,0L101.66,111.03A8,8,0,0,0,101.66,122.34Z"/>
                            </svg>
                            <span>Download</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Generate;